<template>
  <div v-if="!firstLoad">
    <div class="px-4 pt-3
      columns is-multiline flex--justify-space-between flex--align-center">
      <div class="skid__desc column is-two-thirds columns is-multiline">
        <div class="column">
          <label class="help mb-1">Project</label>
          <div>
            {{ skid.project.project_name }}
          </div>
        </div>

        <div class="column">
          <label class="help mb-1">Skid Number</label>
          <div>
            {{ skid.skid_no }}
          </div>
        </div>

        <div class="column">
          <label class="help mb-1">Skid Description</label>
          <v-text-field
            v-if="!skid.sent_on"
            is-small
            placeholder="Skid Description" 
            v-model="skid.desc"
            @blur="updateSkid({ desc: skid.desc })"/>
          <div v-else>
            <span v-if="skid.desc">
              {{ skid.desc }}
            </span>
            <span class="has-text-grey-light" v-else>
              No skid description
            </span>
          </div>
        </div>

        <div v-if="skid.packed_on" class="column">
          <label class="help mb-1">Packed On</label>
          <div>{{ formatDate(parseDate(skid.packed_on)) }}</div>
        </div>

        <div v-if="skid.sent_on" class="column">
          <label class="help mb-1">Sent On</label>
          <div>{{ formatDate(parseDate(skid.sent_on)) }}</div>
        </div>
      </div>
        
      <div class="column noprint">
        <div class="buttons pb-3 flex--justify-end">
          <button 
            v-if="!skid.is_sent"
            class="button is-link" 
            :class="{ 'is-loading': saving }"
            :disabled="saving"
            @click.stop="saveChanges()">
            <span class="icon is-medium">
              <i class="fas fa-save"></i>
            </span>
            <span>              
              Save Skid
            </span>
          </button>
          <button 
            v-if="!skid.packed_on"
            :disabled="skid.items.length == 0"
            class="button is-primary" 
            @click.stop="markDatePacked()">
            <span>              
              Mark as Packed
            </span>
          </button>
          <button 
            v-if="!!skid.packed_on && !skid.is_sent"
            class="button is-primary" 
            @click.stop="markDateSent()">
            <span>              
              Mark as Sent
            </span>
          </button>
          <button
            :disabled="skid.items.length == 0"
            @click="download" 
            class="button">
            <span class="icon">
              <i class="fas fa-download"></i>
            </span>
          </button>
        </div>  
      </div>
    </div>

    <div
      class="skid__items pb-3 noprint">
      <div id="skid-items">
      </div>
    </div>

    <div class="printonly">
      <table class="table is-fullwidth has-smaller-font">
        <thead>
          <tr>
            <th>Piece #</th>
            <th>Installation Order</th>
            <th>Measure</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="(detail, i) in details"
            :key="i">
            <td>{{ detail[1] }}</td>
            <td>{{ detail[2] }}</td>
            <td>{{ detail[3] }}</td>
            <td>{{ detail[4] }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <v-confirm-modal 
      v-if="!firstLoad"
      v-model="confirmModal.state"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :type="confirmModal.type"
      :confirmText="confirmModal.confirmText"
      @confirm="confirmModal.confirmedCb"
      @cancel="confirmModal.state = false">
      <v-datetimepicker 
        :value="parseDate(skid[confirmModal.field])" 
        @input="skid[confirmModal.field] = $event"
        inline/>
    </v-confirm-modal>

  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VConfirmModal from '@/components/base/VConfirmModal'
import VDatetimepicker from '@/components/base/VDatetimepicker'
import { mapState, mapActions, mapMutations } from 'vuex'
import helpers from '@/helpers'

import { downloadCsv } from '@/mixins/downloadable.js'

import jexcel from 'jexcel'
import 'jexcel/dist/jexcel.css'

import { parseISO, format } from 'date-fns'

export default {
  components: {
    VTextField, VConfirmModal, VDatetimepicker
  },
  mixins: [downloadCsv],
  data() {
    return {
      valid: false,
      detailWidth: '500',
      fields: ['id', 'piece_no', 'install_order', 'sqft', 'desc'],
      saving: false,
      originalIds: [],
      confirmModal: {
        field: 'packed_on',
        state: false,
        title: "Enter Date Skid Packed",
        message: "",
        type: "info",
        confirmText: "Save",
        confirmedCb: () => {}
      },
      parseDate: d => {
        return d instanceof Date ? d : d ? parseISO(d) : d
      },
      formatDate: d => {
        return d instanceof Date ? d ? format(d, 'yyyy-MM-dd H:mm:ss') : d : d
      },
      formatCsvDate: d => {
        return d ? format(d, 'yyyy-MM-dd_H.mm.ss') : d
      }
    }
  },
  computed: {
    ...mapState("skid", ["skid", "loading", "firstLoad"]),
    totCols({ fields }) {
      return fields.length
    },
    details({ skid, fields }) {
      let empty = new Array(fields.length);

      if (skid.items.length > 0) {
        let res = skid.items.map(d => {
          return fields.map(field => d[field]);
        });

        return res.length > 0 ? res : [empty];
      } else {
        return [empty];
      }
    },
    options({ detailWidth, totCols }) {
      let cellWidth = detailWidth / (totCols - 1);

      return {
        data: this.details,
        allowInsertColumn: false,
        allowManualInsertColumn: false,
        allowDeleteColumn: false,
        columnResize: false,
        columns: [
          { type: 'numeric', width: `${cellWidth}px`, title: 'ID' },
          { type: 'text', width: `${cellWidth}px`, title: 'Piece #' },
          { type: 'numeric', width: `${cellWidth}px`, title: 'Installation Order' },
          { type: 'numeric', width: `${cellWidth}px`, title: 'Measure (SQFT)' },
          { type: 'text', width: `${cellWidth}px`, title: 'Description' }
        ],
        oninsertrow: () => { this.spreadsheet.hideColumn(0) },
        updateTable: (el, cell, x) => {
          if (this.skid.is_sent) {
            cell.classList.add('readonly');
          }
        } 
      }
    }
  },
  mounted() {
    this.initialize();
  },
  methods: {
    ...mapActions("skid", ["getSkid", "updateSkid", "updateItems", "deleteItem"]),
    ...mapMutations("skid", ["setSkid", "setLoading", "setFirstLoad"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      let skid = this.$route.params.skid;
      if (skid) {
        this.setSkid(skid);
        this.setLoading(false);
        this.setFirstLoad(false);
        this.$nextTick(() => this.initializeSpreadsheet());
      } else {
        this.getSkid(this.$route.params.skidId).then(() => {
          this.initializeSpreadsheet();
        });
      }
    },
    setWidth() {
      let el = document.querySelector('.skid__items');
      this.detailWidth = el.clientWidth - parseFloat(helpers.getStyle(
        el, 'padding-left'
      ).replace("px", "")) - parseFloat(helpers.getStyle(
        el, 'padding-right'
      ).replace("px", "")) - 50;
    },
    setOriginalIds() {
      this.originalIds = this.skid.items.map(d => d.id);
    },
    initializeSpreadsheet() {
      this.setWidth();
      this.setOriginalIds();

      // Initialize railing spreadsheet
      let spreadsheet = jexcel(
        document.getElementById("skid-items"), this.options
      );
      Object.assign(this, { spreadsheet });

      spreadsheet.hideColumn(0);
    },
    getData(validate=true) {
      const {fields, skid} = this;

      let data = this.spreadsheet.getData().map(d => {
        return Object.fromEntries(
          fields.map((_, i) => {
            let v = d[i] == "" ? null : d[i].toString(); 
            return [fields[i], v];
          })
        )
      }).map(d => {
        d.group_id = skid.id;
        return d
      });

      if (validate) {
        return data.filter(d => d.piece_no != null && d.install_order != null && d.sqft != null);
      } else {
        return data
      }
    },
    saveChanges() {
      this.saving = true;

      let data = this.getData();
      let promises = [];

      let newIds = [...data.map(d => +d.id)];
      let deleteIds = this.originalIds.filter(id => !newIds.includes(+id));
      deleteIds.forEach(deleteId => {
        promises.push(this.deleteItem(deleteId));
      });

      promises.push(this.updateItems(data));

      Promise.all(promises).then(resp => {
        this.setNotification({
          status: true,
          type: 'success',
          message: 'Skid successfully updated'
        });
        this.reFetch();
      }).catch(() => {
        this.reFetch();
      })
    },
    reFetch() {
      this.getSkid(this.$route.params.skidId).then(() => {
        this.reset();
        this.refreshTable();
        this.saving = false;
      })
    },
    sortByInstallOrder() {
      let data = this.spreadsheet.getData();
      if (data.length > 0) {
        let installOrderIndex = this.fields.indexOf('install_order');
        data.sort((a,b) => +a[installOrderIndex] - +b[installOrderIndex]);
        this.refreshTable(data);
      }
    },
    reset() {
      this.setOriginalIds();
    },
    refreshTable(data) {
      data = data ? data : this.details;
      this.spreadsheet.setData(data);
      this.spreadsheet.refresh();
      this.spreadsheet.hideColumn(0);
    },
    markDatePacked() {
      this.confirmModal.field = 'packed_on';
      this.confirmModal.confirmedCb = () => {
        this.updateDatePacked().then(() => {
          this.confirmModal.state = false;
        })
      }
      this.confirmModal.state = true;
    },
    updateDatePacked() {
      return new Promise((resolve, reject) => {
        this.updateSkid({ packed_on: this.skid.packed_on }).then(resp => {
          this.setNotification({
            status: true,
            type: 'success',
            message: 'Skid successfully marked as packed'
          });
          resolve(resp)
        }).catch(err => reject(err))
      })
    },
    markDateSent() {
      this.confirmModal.field = 'sent_on';
      this.confirmModal.title = 'Enter Date Skid Sent'
      this.confirmModal.confirmedCb = () => {
        this.updateDateSent().then(() => {
          this.confirmModal.state = false
          this.spreadsheet.updateTable(
            function (el, cell, x) { 
              cell.classList.add('readonly');
            }
          )
        })
      }
      this.confirmModal.state = true;
    },
    updateDateSent() {
      return new Promise((resolve, reject) => {
        this.updateSkid({ is_sent: true, sent_on: this.skid.sent_on }).then(resp => {
          this.setNotification({
            status: true,
            type: 'success',
            message: 'Skid successfully marked as sent'
          });
          resolve(resp)
        }).catch(err => reject(err))
      })
    },
    download() {
      let data = this.getData();

      this.downloadCsv({ 
        data: data.map(d => {
          let res = {}
          res['Project Name'] = this.skid.project.project_name;
          res['Skid/ Group #'] = this.skid.skid_no;
          res['Packed On'] = this.formatDate(this.parseDate(this.skid.packed_on));
          res['Sent On'] = this.formatDate(this.parseDate(this.skid.sent_on));
          let otherFields = [
            { field: 'piece_no', label: 'Piece #'},
            { field: 'install_order', label: 'Installation Order'},   
            { field: 'sqft', label: 'Measure (SQFT)'},   
            { field: 'desc', label: 'Description' }
          ]
          
          otherFields.forEach(o => {
            res[o.label] = d[o.field];
          })

          return res
        }), 
        filename: `defected_items_${this.formatCsvDate(new Date)}.csv` 
      });
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';
@import '@/styles/printable.scss';

.skid__items {
  width: 100%;
  min-height: 100vh;
  overflow-x: auto;
  overflow-y: visible;
  padding: 0em 1em;
}

@media print {
  .skid__desc {
    font-size: 0.7em;
    width: 100% !important;
  }
}
</style>